import type React from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { BreadcrumbItem } from '../ui/Breadcrumbs.js';
import { PageHeader, type PageHelpContent } from '../ui/PageHeader.js';

export interface SettingsNavItem {
  /** The section's own URL: the active item follows the address bar. */
  to: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface SettingsPageProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  helpContent?: PageHelpContent;
  actions?: React.ReactNode;
  nav: readonly SettingsNavItem[];
  /** The active section's content (an account form, a `SettingList`). */
  children: React.ReactNode;
}

/**
 * The settings page template: a section nav on the left (icon tile, title, one-line description) and the active
 * section on the right; below `md` the nav stacks above the content. Template example: NextAdmin `profile/layout.tsx`
 * (demo `/profile`).
 */
export function SettingsPage({ title, subtitle, breadcrumbs, helpContent, actions, nav, children }: SettingsPageProps) {
  const { pathname } = useLocation();

  return (
    <div className="space-y-5">
      <PageHeader
        title={title}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
        helpContent={helpContent}
        actions={actions}
      />
      <div className="flex max-w-full flex-col gap-x-0 gap-y-6 rounded-xl border-[0.5px] border-card-border bg-card-background p-0 md:flex-row md:gap-y-8 lg:min-h-150">
        {/* Section navigation */}
        <nav
          aria-label={`${title} sections`}
          className="flex w-full shrink-0 grow flex-col gap-2 self-stretch border-card-border px-3 py-6 lg:max-w-84.5 lg:border-r"
        >
          {nav.map((item) => {
            const isActive = pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-active={isActive}
                aria-current={isActive ? 'page' : undefined}
                className="flex w-full items-start justify-start gap-3 rounded-xl p-2 hover:bg-background-gray-secondary_alt/45 data-[active=true]:border-none data-[active=true]:bg-background-gray-secondary_alt"
              >
                <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border-secondary-alt bg-background-gray-secondary_alt text-icon-secondary">
                  {item.icon}
                </div>
                <div className="flex flex-col items-start gap-1">
                  <span className="text-sm font-semibold text-text-primary">{item.title}</span>
                  <span className="text-xs font-normal text-text-tertiary">{item.description}</span>
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Content */}
        <div className="w-full flex-1 p-6">{children}</div>
      </div>
    </div>
  );
}
